import React, { ReactNode, useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Item } from './multi-select';

import { useTranslation } from 'react-i18next';

export const DisplayButton = <T extends Item>({
  value,
  selectText,
  label,
  options,
  icon: Icon,
  displayMaxItems,
  onClose,
  permanent,
  operator,
  showOperator = true,
  defaultSelectedOptions,
  displayButtonClassName,
  selectedLabelClassName = 'max-w-30',
  clearFilter,
  showInlineClear,
  iconPosition = 'left',
  dataTestId,
}: {
  value?: string | string[];
  options?: T[];
  selectText?: string | ReactNode;
  label?: string;
  icon?: React.FC<{ className?: string }>;
  displayMaxItems: number;
  onClose?: () => void;
  permanent?: boolean;
  operator?: string | ReactNode;
  showOperator?: boolean;
  defaultSelectedOptions?: T[];
  displayButtonClassName?: string;
  selectedLabelClassName?: string;
  clearFilter?: () => void;
  showInlineClear?: boolean;
  iconPosition?: 'left' | 'right';
  dataTestId?: string;
}) => {
  const { t } = useTranslation('designSystem');
  const isEmpty = !value || (Array.isArray(value) && value.length < 1);
  let values: string[] = [];
  if (!isEmpty) {
    values = values.concat(typeof value === 'string' ? [value] : value);
  }
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);
  const displayValues = values.slice(0, displayMaxItems);

  useEffect(() => {
    setSelectedOptions((prevOptions) => {
      if (values?.length === 0) {
        return [];
      }
      let newOptions: T[] = [...prevOptions];
      values.map((value) => {
        const option = options?.find((option) => option.value === value);
        if (option) {
          const exists = newOptions.some(
            (existingOption) => existingOption.value === value,
          );
          if (exists) {
            newOptions = newOptions.filter(
              (existingOption) => existingOption.value !== value,
            );
          }
          newOptions.push(option);
        } else {
          const defaultOption = defaultSelectedOptions?.find(
            (option) => option.value === value,
          );
          if (defaultOption) {
            newOptions.push(defaultOption);
          }
        }
      });
      return newOptions;
    });
  }, [options, defaultSelectedOptions]);

  return (
    <div
      className={`flex h-[40px] items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-normal ${displayButtonClassName}`}
    >
      <Combobox.Button as="div" className="h-full w-full overflow-hidden">
        <button
          type="button"
          {...(dataTestId
            ? { 'data-testid': dataTestId }
            : { 'data-testid': 'dropdown-display-button' })}
          className="flex h-full w-full items-center gap-2 overflow-hidden p-2 overflow-ellipsis"
        >
          {Icon && iconPosition === 'left' && <Icon className="h-4 w-4" />}
          {isEmpty ? (
            selectText || (
              <span className="">
                {' '}
                <span className="text-gray-600 dark:text-gray-400">
                  {t('select')}
                </span>{' '}
                <span className="font-medium">{label}</span>
              </span>
            )
          ) : (
            <>
              {showOperator && (
                <div className="whitespace-nowrap">
                  <span className="font-medium">{label}</span>{' '}
                  <span className="text-gray-600 dark:text-gray-400">
                    {operator
                      ? operator
                      : values.length > 1
                        ? t('in')
                        : t('is')}
                  </span>{' '}
                </div>
              )}
              <div className="flex min-w-0 gap-1.5">
                {displayValues.map((value, index) => {
                  const item = selectedOptions?.find(
                    (option) => option?.value === value,
                  );
                  const render =
                    item?.selectedDisplay || item?.label || item?.value;
                  return (
                    <span key={value} className="flex min-w-0">
                      <span
                        className={`block min-w-0 truncate ${selectedLabelClassName}`}
                      >
                        <span className="font-medium">{render}</span>
                      </span>
                      {typeof render !== 'string' ||
                      index === displayValues.length - 1
                        ? ''
                        : ', '}
                    </span>
                  );
                })}
                {values.length > displayValues.length && (
                  <span className="flex shrink-0 items-center">
                    <span className="-ml-[2px] whitespace-nowrap">
                      {t('and')}{' '}
                      <span className="font-medium">
                        {values.length - displayMaxItems}+
                      </span>{' '}
                      {t('values')}
                    </span>
                  </span>
                )}
              </div>
            </>
          )}
          {Icon && iconPosition === 'right' && (
            <Icon className="ml-auto h-4 w-4" />
          )}
        </button>
      </Combobox.Button>
      {onClose && !permanent && (
        <button
          data-testid={
            dataTestId ? `${dataTestId}-close-button` : 'dropdown-close-button'
          }
          className="flex items-center self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
          onClick={onClose}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
      {value && value.length > 0 && clearFilter && showInlineClear && (
        <button
          type="button"
          aria-label={t('clear')}
          data-testid="dropdown-clear-button"
          className="flex items-center self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
          onClick={clearFilter}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
